import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../i18n/useTranslation';
import { Card } from '../atoms/Card';
import { Stack } from '../atoms/Stack';
import { Inline } from '../atoms/Inline';
import { Grid } from '../atoms/Grid';
import { Icon } from '../atoms/Icon';
import { Badge } from '../atoms/Badge';
import { Button } from '../atoms/Button';
import { EmptyState } from '../atoms/EmptyState';
import { Text } from '../atoms/Typography';
import { Section } from '../templates/Section';
import { ConfirmDialog } from '../ui/ConfirmDialog/ConfirmDialog';
import { useAquariumMemory } from '../../hooks/useAquariumMemory';
import type { AquariumMemoryItem, MemorySourceFeature } from '../../services/memoryService';
import './AquariumMemorySection.css';
import { Alert } from '../atoms/Alert';
import { LiveRegion } from '../atoms/LiveRegion';
import { LoadErrorState } from '../feedback/LoadErrorState/LoadErrorState';

export interface AquariumMemorySectionProps {
  readonly aquariumId: string;
}

const SOURCE_LABEL_KEY: Record<MemorySourceFeature, string> = {
  reef_insight: 'memory.source.reefInsight',
  journal_analysis: 'memory.source.journalAnalysis',
  image_insight: 'memory.source.imageInsight',
  ai_output_feedback: 'memory.source.aiOutputFeedback',
};

type PendingConfirm = { readonly kind: 'delete'; readonly item: AquariumMemoryItem } | { readonly kind: 'clear-all' } | null;

/**
 * "What the AI knows about this tank" (#1451): lists the aquarium's persistent
 * AI memory and lets the owner delete single facts or clear all of it.
 * Confirmation, focus trap and focus restore come from ConfirmDialog; results
 * are announced in a polite live region and shown as text (never colour only).
 */
export function AquariumMemorySection({ aquariumId }: AquariumMemorySectionProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { items, isLoading, isError, refetch, deleteItem, isDeletingItem, clearAll, isClearingAll } =
    useAquariumMemory(aquariumId);

  const [pending, setPending] = useState<PendingConfirm>(null);
  const [mutationError, setMutationError] = useState<'delete' | 'clearAll' | null>(null);
  const [liveMessage, setLiveMessage] = useState('');

  const confirmDelete = async (item: AquariumMemoryItem): Promise<void> => {
    setPending(null);
    try {
      await deleteItem(item.id);
      setMutationError(null);
      setLiveMessage(t('memory.live.deleteSuccess'));
    } catch {
      setMutationError('delete');
      setLiveMessage(t('memory.live.deleteError'));
    }
  };

  const confirmClearAll = async (): Promise<void> => {
    setPending(null);
    try {
      await clearAll();
      setMutationError(null);
      setLiveMessage(t('memory.live.clearAllSuccess'));
    } catch {
      setMutationError('clearAll');
      setLiveMessage(t('memory.live.clearAllError'));
    }
  };

  const clearAllAction =
    items.length > 0 ? (
      <Button
        variant="secondary"
        onClick={() => {
          setMutationError(null);
          setPending({ kind: 'clear-all' });
        }}
        disabled={isClearingAll}
      >
        {t('memory.action.clearAll')}
      </Button>
    ) : undefined;

  let body;
  if (isLoading) {
    body = (
      <Grid minItemWidth="md" aria-busy="true">
        <Card>
          <Text variant="muted">{t('memory.loading.text')}</Text>
        </Card>
      </Grid>
    );
  } else if (isError) {
    body = (
      <LoadErrorState
        title={t('memory.error.text')}
        onRetry={() => void refetch()}
        retryLabel={t('memory.error.retry')}
      />
    );
  } else if (items.length === 0) {
    body = (
      <EmptyState
        icon={<Icon name="brain" />}
        headline={t('memory.empty.title')}
        description={t('memory.empty.description')}
        actionLabel={t('memory.empty.action')}
        onAction={() => navigate('/dashboard')}
        headingLevel={3}
      />
    );
  } else {
    body = (
      <Grid as="ul" minItemWidth="md" className="aquarium-memory__list">
        {items.map((item) => (
          <li key={item.id}>
            <Card>
              <Inline justify="between" align="start" gap="4" wrap={false}>
                <Stack gap="2">
                  <Text>{t('memory.item.fact.text', { factText: item.factText })}</Text>
                  <Inline gap="2">
                    <Badge tone="ai">{t(SOURCE_LABEL_KEY[item.sourceFeature] ?? 'memory.source.reefInsight')}</Badge>
                    <Text variant="caption">
                      {t('memory.item.timestamp.text', { date: new Date(item.createdAt).toLocaleString() })}
                    </Text>
                    {item.confidence !== null && (
                      <Text variant="caption">
                        {t('memory.item.confidence.text', { confidence: Math.round(item.confidence * 100) })}
                      </Text>
                    )}
                  </Inline>
                </Stack>
                <Button
                  variant="ghost"
                  aria-label={t('memory.action.deleteItem.ariaLabel', { fact: item.factText })}
                  onClick={() => {
                    setMutationError(null);
                    setPending({ kind: 'delete', item });
                  }}
                  disabled={isDeletingItem}
                >
                  {t('memory.action.delete')}
                </Button>
              </Inline>
            </Card>
          </li>
        ))}
      </Grid>
    );
  }

  return (
    <Section
      title={t('memory.section.title')}
      description={t('memory.section.description')}
      actions={clearAllAction}
      data-testid="aquarium-memory-section"
    >
      <Stack gap="4">
        {mutationError && (
          <Alert>
            {mutationError === 'delete' ? t('memory.error.delete.text') : t('memory.error.clearAll.text')}
          </Alert>
        )}
        {body}
        <LiveRegion className="aquarium-memory__status">{liveMessage}</LiveRegion>
      </Stack>

      <ConfirmDialog
        isOpen={pending?.kind === 'delete'}
        title={t('memory.dialog.delete.title')}
        message={t('memory.dialog.delete.description')}
        confirmLabel={t('memory.dialog.delete.confirm')}
        cancelLabel={t('memory.dialog.delete.cancel')}
        onConfirm={() => {
          if (pending?.kind === 'delete') void confirmDelete(pending.item);
        }}
        onCancel={() => setPending(null)}
      />
      <ConfirmDialog
        isOpen={pending?.kind === 'clear-all'}
        title={t('memory.dialog.clearAll.title')}
        message={t('memory.dialog.clearAll.description')}
        confirmLabel={t('memory.dialog.clearAll.confirm')}
        cancelLabel={t('memory.dialog.delete.cancel')}
        onConfirm={() => void confirmClearAll()}
        onCancel={() => setPending(null)}
      />
    </Section>
  );
}

export default AquariumMemorySection;
